// Dialogs: Settings (six tabs with a scope choice), About (case checksum), tax form viewer, Compare with Xspouse,
// timeshare worksheet (hybrid-app component) and Open case.
import { lazy, Suspense, useEffect, useState } from 'react';
import { Dlg } from './Dlg';
import type { Case } from '@divorceos/schema';
import { useCaseView } from '../caseView';
import { caseChecksum } from '../checksum';
import { APP_VERSION, ENGINE_VERSION, programDefaults, restoreDefaults, saveProgramDefaults, saveSessionDefaults } from '../model';
import { useActiveDoc, useDis, type SettingsTab } from '../store';
import { CsSettings, PartySettings, ProgramSettings, SsSettings, TacticSettings, TaxSettings } from '../tabs/Sections';

import { useReferenceStore } from '../../reference/referenceStore';

// Heavier dialogs load on first use (code splitting).
const ReferencePanel = lazy(() => import('../../reference/ReferencePanel').then((m) => ({ default: m.ReferencePanel })));
const TimeshareWorksheetDialog = lazy(() => import('../../timeshare/TimeshareWorksheetDialog').then((m) => ({ default: m.TimeshareWorksheetDialog })));
const QuestionnaireDialog = lazy(() => import('../questionnaire/QuestionnaireDialog').then((m) => ({ default: m.QuestionnaireDialog })));
const ImportDialog = lazy(() => import('../import/ImportDialog').then((m) => ({ default: m.ImportDialog })));
const TaxFormsTable = lazy(() => import('../reports/Analysis').then((m) => ({ default: m.TaxFormsTable })));
const Loading = () => <div className="scrim"><div className="dlg sm"><div className="pad muted">Loading…</div></div></div>;

const SET_TABS: [SettingsTab, string][] = [['cs', 'Child Support'], ['ss', 'Spousal Support'], ['tax', 'Taxes'], ['tac', 'Tactics'], ['info', 'Case Information'], ['prog', 'Program']];

function SettingsDialog() {
  const v = useCaseView();
  const tab = useDis((s) => s.ui.settingsTab);
  const setUi = useDis((s) => s.setUi);
  const update = useDis((s) => s.update);
  const toast = useDis((s) => s.toast);
  const [snapshot] = useState<Case>(() => v.c);
  const [scope, setScope] = useState<'case' | 'session' | 'defaults'>('case');
  const close = () => setUi({ dialog: null });
  const cancel = () => { update((x) => { Object.assign(x, structuredClone(snapshot)); }); close(); };
  const ok = () => {
    if (scope === 'defaults') { saveProgramDefaults(v.c.settings); toast('Settings applied and saved as your defaults for new cases.'); }
    else if (scope === 'session') { saveSessionDefaults(v.c.settings); toast('Settings applied; new cases use them until you close disOS.'); }
    else toast('Settings applied to this case.');
    close();
  };
  const counts = (t: SettingsTab) => v.changed.filter((x) => x.tab === t).length;
  return (
    <Dlg title={`Settings · ${v.doc.name || 'Untitled case'}`} onClose={cancel} footer={<>
      <div className="scope" role="radiogroup" aria-label="Apply to">
        <label><input type="radio" name="scope" checked={scope === 'case'} onChange={() => setScope('case')} /> This case only</label>
        <label><input type="radio" name="scope" checked={scope === 'session'} onChange={() => setScope('session')} /> Until I close disOS</label>
        <label><input type="radio" name="scope" checked={scope === 'defaults'} onChange={() => setScope('defaults')} /> Save as my defaults</label>
      </div>
      <span className="sp" />
      <button type="button" className="btn" onClick={() => update((x) => restoreDefaults(x, programDefaults()))}>Restore defaults</button>
      <button type="button" className="btn" onClick={cancel}>Cancel</button>
      <button type="button" className="btn pri" onClick={ok}>OK</button>
    </>}>
      <div className="dbody">
        <div className="dtabs" role="tablist">
          {SET_TABS.map(([id, l]) => <button key={id} type="button" role="tab" className={tab === id ? 'on' : ''} onClick={() => setUi({ settingsTab: id })}>{l}{counts(id) > 0 && <span className="c">{counts(id)}</span>}</button>)}
        </div>
        <div className="dcontent">
          {tab === 'cs' && <CsSettings />}
          {tab === 'ss' && <SsSettings />}
          {tab === 'tax' && <TaxSettings />}
          {tab === 'tac' && <TacticSettings />}
          {tab === 'info' && <PartySettings />}
          {tab === 'prog' && <ProgramSettings />}
        </div>
      </div>
    </Dlg>
  );
}

function AboutDialog() {
  const doc = useActiveDoc();
  const setUi = useDis((s) => s.setUi);
  const [sum, setSum] = useState<{ full: string; short: string } | null>(null);
  useEffect(() => { if (doc) void caseChecksum(doc.calcData, ENGINE_VERSION).then(setSum); }, [doc]);
  const close = () => setUi({ dialog: null });
  return (
    <Dlg title="About disOS" size="sm" onClose={close} footer={<><span className="sp" /><button type="button" className="btn pri" onClick={close}>Close</button></>}>
      <div className="pad">
        <p style={{ marginTop: 0 }}><b>{APP_VERSION}</b><br />California guideline support in a desktop-style workflow. Working title, pending a trademark review.<br /><span className="muted">Engine: {ENGINE_VERSION}</span></p>
        {doc && (<>
          <p>Case checksum for <b>{doc.name || 'Untitled case'}</b>:</p>
          <p style={{ font: '500 20px var(--mono)', letterSpacing: '.04em', margin: '4px 0 6px' }}>{sum?.short ?? '…'}</p>
          <p className="muted" style={{ fontSize: 11, wordBreak: 'break-all', margin: '0 0 6px' }}>SHA-256 {sum?.full ?? ''}</p>
          <p className="muted" style={{ fontSize: 12 }}>SHA-256 of the canonical case data (keys sorted, record id and name left out) plus the engine version. Results are a pure function of both, so two people with the same checksum entered the same case and got the same answer.</p>
        </>)}
        <p className="muted" style={{ fontSize: 12 }}>Not certified by the Judicial Council (CRC 5.275).</p>
      </div>
    </Dlg>
  );
}

function FormsDialog() {
  const v = useCaseView();
  const fp = useDis((s) => s.ui.formParty);
  const setUi = useDis((s) => s.setUi);
  const close = () => setUi({ dialog: null });
  return (
    <Dlg title="Tax form viewer" size="md" onClose={close}>
      <div className="pad" style={{ display: 'flex', gap: 10, alignItems: 'center', borderBottom: '1px solid var(--line)' }}>
        <div className="seg">{([0, 1] as const).map((p) => <button key={p} type="button" className={fp === p ? 'on' : ''} onClick={() => setUi({ formParty: p })}>{v.labels[p]}</button>)}</div>
        <span className="muted">Annual form lines from the engine, Guideline and Proposed.</span>
        <span style={{ flex: 1 }} />
        <button type="button" className="btn sm" onClick={() => setUi({ dialog: null, left: 'reports', report: 'forms' })}>Open as report</button>
      </div>
      <div className="dcontent" style={{ padding: '0 16px 16px' }}>
        <div className="paper" style={{ position: 'static', width: 'auto', minHeight: 0, boxShadow: 'none', padding: '12px 0' }}><Suspense fallback={<p className="muted">Loading…</p>}><TaxFormsTable v={v} party={fp} /></Suspense></div>
      </div>
    </Dlg>
  );
}

function CompareDialog() {
  const setUi = useDis((s) => s.setUi);
  const setEnabled = useReferenceStore((s) => s.setEnabled);
  useEffect(() => { setEnabled(true); return () => setEnabled(false); }, [setEnabled]);
  const close = () => setUi({ dialog: null });
  return (
    <Dlg title="Compare with Xspouse" size="md" onClose={close}>
      <div className="dcontent" style={{ padding: '8px 16px 16px' }}>
        <p className="muted" style={{ margin: '0 0 6px' }}>Sends this case to the original Xspouse engine (reference bridge, POST /Xspouse/api/reference/calc) and lists the differences. disOS uses the published IRS/FTB tables and, from 1 Sep 2024, the amended FC 4055; Xspouse 2023 uses its own tables and the earlier formula, so some differences are expected.</p>
        <Suspense fallback={<p className="muted">Loading…</p>}><ReferencePanel /></Suspense>
      </div>
    </Dlg>
  );
}

function OpenDialog() {
  const recent = useDis((s) => s.recent);
  const openCase = useDis((s) => s.openCase);
  const setUi = useDis((s) => s.setUi);
  const [q, setQ] = useState('');
  const close = () => setUi({ dialog: null });
  const list = recent.filter((r) => `${r.name ?? ''} ${r.caseNumber ?? ''} ${(r.partyNames ?? []).join(' ')}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <Dlg title="Open case" size="sm" onClose={close}>
      <div className="pad">
        <input type="text" placeholder="Filter by name, case number or party" value={q} onChange={(e) => setQ(e.target.value)} style={{ width: '100%', height: 28, border: '1px solid var(--line)', borderRadius: 5, padding: '0 8px', marginBottom: 8 }} autoFocus />
        <table className="recent"><tbody>
          {list.map((r) => <tr key={r.id} className="r" tabIndex={0} onClick={() => { close(); void openCase(r.id); }} onKeyDown={(e) => { if (e.key === 'Enter') { close(); void openCase(r.id); } }}><td className="nm">{r.name || 'Untitled case'}</td><td className="muted">{r.caseNumber}</td></tr>)}
          {list.length === 0 && <tr><td className="muted">No cases.</td></tr>}
        </tbody></table>
      </div>
    </Dlg>
  );
}

export function Dialogs({ inCase }: { inCase: boolean }) {
  const dialog = useDis((s) => s.ui.dialog);
  const arg = useDis((s) => s.ui.dialogArg);
  const setUi = useDis((s) => s.setUi);
  if (dialog === 'about') return <AboutDialog />;
  if (dialog === 'open') return <OpenDialog />;
  if (!inCase) return null;
  if (dialog === 'settings') return <SettingsDialog />;
  if (dialog === 'forms') return <FormsDialog />;
  if (dialog === 'compare') return <CompareDialog />;
  if (dialog === 'worksheet') return <Suspense fallback={<Loading />}><TimeshareWorksheetDialog childIndex={arg} onClose={() => setUi({ dialog: null })} /></Suspense>;
  if (dialog === 'questionnaire') return <Suspense fallback={<Loading />}><QuestionnaireDialog /></Suspense>;
  if (dialog === 'import') return <Suspense fallback={<Loading />}><ImportDialog /></Suspense>;
  return null;
}
